<!--
  Cambio partner e sede della prenotazione, solo Simba
  (PATCH /booking/:id/partner-sede).

  Si sceglie il partner e poi una sua sede. Scelta la sede, il server calcola
  cosa cambia (`dryRun`): km e prezzo di ogni tratta col listino del nuovo
  partner e la nuova sede, il totale, i turni di chi è assegnato, il valet.
  Solo dopo si salva. Se con i nuovi orari chi è assegnato non è più libero
  lo si può togliere dalle tratte.
-->
<template>
  <el-dialog
    :model-value="modelValue"
    title="Cambia partner e sede"
    width="min(680px, 96vw)"
    @update:model-value="$emit('update:modelValue', $event)"
  >
    <div class="psd">
      <div class="psd__sel">
        <div class="psd__fld">
          <label>Partner</label>
          <el-select
            v-model="partnerId"
            placeholder="Cerca il partner…"
            filterable
            remote
            reserve-keyword
            :remote-method="cerca"
            :loading="cercando"
            style="width: 100%"
            @change="onPartner"
          >
            <el-option
              v-for="p in partner"
              :key="p.id"
              :label="p.is_restaurant ? `${p.company_name} · notturno` : p.company_name"
              :value="p.id"
            />
          </el-select>
        </div>
        <div class="psd__fld">
          <label>Sede</label>
          <el-select
            v-model="sedeId"
            placeholder="Scegli la sede"
            :disabled="!partnerId"
            :loading="caricandoSedi"
            style="width: 100%"
            @change="calcola"
          >
            <el-option
              v-for="s in sedi"
              :key="s.id"
              :label="s.name || s.city"
              :value="s.id"
            >
              <span>{{ s.name || s.city }}</span>
              <span class="psd__opt">{{ indirizzo(s) }}</span>
            </el-option>
          </el-select>
          <p
            v-if="partnerId && !caricandoSedi && !sedi.length"
            class="psd__hint"
          >
            Il partner non ha sedi.
          </p>
        </div>
      </div>

      <el-checkbox
        v-if="pickup && cambiaPartner"
        v-model="usaValet"
        @change="calcola"
      >
        Usa il pacchetto valet dell'auto col nuovo partner, se ne ha uno valido
      </el-checkbox>

      <p
        v-if="calcolando && !anteprima"
        class="psd__hint"
      >
        Calcolo di cosa cambia…
      </p>

      <!-- ---- confronto prima / dopo ---- -->
      <div
        v-if="anteprima"
        class="psd__cmp"
        :class="{ 'is-loading': calcolando }"
      >
        <div class="psd__row psd__row--head">
          <span />
          <span>Prima</span>
          <span>Dopo</span>
        </div>
        <div class="psd__row">
          <span>Partner</span>
          <span>{{ anteprima.partner.prima.nome || '—' }}</span>
          <b :class="{ 'psd__chg': anteprima.cambiaPartner }">{{ anteprima.partner.dopo.nome }}</b>
        </div>
        <div class="psd__row">
          <span>Sede</span>
          <span>{{ anteprima.sede.prima?.nome || '—' }}</span>
          <b class="psd__chg">{{ anteprima.sede.dopo.nome }}</b>
        </div>

        <template
          v-for="t in anteprima.tratte"
          :key="t.id"
        >
          <div class="psd__leg">
            {{ t.servizio }} · {{ t.verso === 'return' ? 'ritorno' : 'andata' }}
          </div>
          <div class="psd__row">
            <span>Km</span>
            <span>{{ numero(t.km.prima) }}</span>
            <b :class="{ 'psd__chg': cambia(t.km) }">{{ numero(t.km.dopo) }}</b>
          </div>
          <div class="psd__row">
            <span>Prezzo della tratta</span>
            <span>{{ euro(t.prezzo.prima) }}</span>
            <b :class="{ 'psd__chg': cambia(t.prezzo) }">{{ euro(t.prezzo.dopo) }}</b>
          </div>
          <template v-if="t.turni">
            <div
              v-for="r in turniDi(t)"
              :key="r.ruolo"
              class="psd__row"
            >
              <span>{{ r.ruolo }}<em> · {{ r.persona || 'da assegnare' }}</em></span>
              <span>{{ orario(r.prima) }}</span>
              <b :class="{ 'psd__chg': orario(r.prima) !== orario(r.dopo) }">{{ orario(r.dopo) }}</b>
            </div>
          </template>
          <p
            v-else-if="t.turniInvariati"
            class="psd__hint"
          >
            Tratta già iniziata: i turni restano com'erano.
          </p>
        </template>

        <!-- Imponibile e totale: il ricalcolo li propone, Simba li può
             correggere prima di salvare. Cambiandone uno l'altro segue con
             l'IVA al 22%. -->
        <div class="psd__row psd__row--tot psd__row--in">
          <span>Imponibile prenotazione</span>
          <span>{{ euro(anteprima.prenotazione.imponibile.prima) }}</span>
          <el-input
            v-model="imp"
            size="small"
            class="psd__in"
            aria-label="Imponibile dopo il cambio"
            @input="onImp"
          >
            <template #prepend>
              €
            </template>
          </el-input>
        </div>
        <div class="psd__row psd__row--in">
          <span>Totale fattura</span>
          <span>{{ euro(anteprima.prenotazione.totale.prima) }}</span>
          <el-input
            v-model="tot"
            size="small"
            class="psd__in"
            aria-label="Totale fattura dopo il cambio"
            @input="onTot"
          >
            <template #prepend>
              €
            </template>
          </el-input>
        </div>
        <p
          v-if="importiToccati"
          class="psd__hint"
        >
          Calcolati: imponibile {{ euro(anteprima.prenotazione.imponibile.dopo) }},
          totale {{ euro(anteprima.prenotazione.totale.dopo) }}.
          <el-button
            text
            size="small"
            @click="ripristinaImporti"
          >
            Rimetti i calcolati
          </el-button>
        </p>

        <!-- Pagamento in app della corsa: la voce «Prenotazione»/«Transfer»
             da pagare. Di default il totale fattura; si può cambiare o non
             chiederlo. Le voci già pagate e le lavorazioni non si toccano. -->
        <div class="psd__row psd__row--in psd__row--app">
          <el-checkbox v-model="inApp">
            Il cliente paga in app
          </el-checkbox>
          <span>{{ anteprima.pagamentoInApp?.voce ? euro(anteprima.pagamentoInApp.voce.importo) : 'nessuna' }}</span>
          <el-input
            v-model="importoApp"
            size="small"
            class="psd__in"
            :disabled="!inApp"
            aria-label="Quanto paga in app"
            @input="appToccato = true"
          >
            <template #prepend>
              €
            </template>
          </el-input>
        </div>
        <p class="psd__hint">
          <template v-if="!inApp && anteprima.pagamentoInApp?.voce">
            La voce da pagare in app ({{ euro(anteprima.pagamentoInApp.voce.importo) }}) si toglie.
          </template>
          <template v-else-if="inApp">
            Di default il totale fattura.
          </template>
          <template v-else>
            Nessun pagamento in app per la corsa.
          </template>
          <template v-if="anteprima.pagamentoInApp?.pagato">
            Già pagati in app {{ euro(anteprima.pagamentoInApp.pagato) }}: restano.
          </template>
        </p>

        <p
          v-if="anteprima.prezzoManuale"
          class="psd__hint"
        >
          Il prezzo di questo Transfer è deciso a mano: resta quello.
        </p>
        <p
          v-if="valetTesto"
          class="psd__hint"
        >
          {{ valetTesto }}
        </p>
        <p
          v-if="anteprima.importiCod3"
          class="psd__hint psd__hint--attn"
        >
          Nel COD3 questa prenotazione ha un importo corretto a mano: vale quello.
        </p>
        <p
          v-if="anteprima.provvigione"
          class="psd__hint"
        >
          La provvigione resta al venditore che ha venduto la prenotazione.
        </p>
        <p
          v-if="anteprima.cambiaPartner"
          class="psd__hint"
        >
          Il cliente diventa anche cliente di {{ anteprima.partner.dopo.nome }}.
        </p>
        <div
          v-if="anteprima.motivo || liberaDriver"
          class="psd__warn"
        >
          <p v-if="anteprima.motivo">
            Con la nuova sede: {{ anteprima.motivo }}.
          </p>
          <el-checkbox
            v-model="liberaDriver"
            @change="calcola"
          >
            Togli i driver dalle tratte e ricalcola i turni con chi è libero
          </el-checkbox>
        </div>
      </div>
    </div>

    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">
        Annulla
      </el-button>
      <el-button
        type="primary"
        :disabled="!anteprima || !!anteprima.motivo || calcolando || !importiValidi"
        :loading="salvando"
        @click="salva"
      >
        Salva
      </el-button>
    </template>
  </el-dialog>
</template>

<script>
import { computed, ref, watch } from 'vue';
import { ElMessage } from 'element-plus';
import bookingAPI from '../../services/bookingAPI.js';
import partnerAPI from '../../services/partnerAPI.js';
import { headquartersOfPartner } from '../../services/valetAPI.js';

export default {
  name: 'PartnerSedeDialog',
  props: {
    modelValue: { type: Boolean, default: false },
    booking: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'saved'],
  setup(props, { emit }) {
    const partnerId = ref('');
    const sedeId = ref('');
    const partner = ref([]);
    const sedi = ref([]);
    const cercando = ref(false);
    const caricandoSedi = ref(false);
    const calcolando = ref(false);
    const salvando = ref(false);
    const anteprima = ref(null);
    const liberaDriver = ref(false);
    const usaValet = ref(false);
    // Importi e pagamento in app scelti prima di salvare (testo, con la
    // virgola come la scrive chi usa il gestionale).
    const imp = ref('');
    const tot = ref('');
    const importiToccati = ref(false);
    const inApp = ref(false);
    const importoApp = ref('');
    const appToccato = ref(false);
    let primaAnteprima = true;

    const partnerPrima = computed(() => props.booking?.headquarter?.partner || null);
    const pickup = computed(() =>
      ['pickup', 'pickup_loaner'].includes(props.booking?.type_prenotazione));
    const cambiaPartner = computed(() => !!partnerId.value && partnerId.value !== partnerPrima.value?.id);

    const indirizzo = (s) =>
      [[s?.address, s?.street_number].filter(Boolean).join(' '), s?.city].filter(Boolean).join(', ');

    const caricaSedi = async (id) => {
      sedi.value = [];
      if (!id) return;
      caricandoSedi.value = true;
      try {
        sedi.value = await headquartersOfPartner(id);
      } catch (e) {
        console.warn('Sedi del partner non caricate:', e?.message);
      } finally {
        caricandoSedi.value = false;
      }
    };

    watch(() => props.modelValue, (open) => {
      if (!open || !props.booking) return;
      const p = partnerPrima.value;
      partner.value = p ? [p] : [];
      partnerId.value = p?.id || '';
      sedeId.value = '';
      anteprima.value = null;
      liberaDriver.value = false;
      usaValet.value = !!props.booking.valet_consumed;
      importiToccati.value = false;
      appToccato.value = false;
      primaAnteprima = true;
      caricaSedi(partnerId.value);
    }, { immediate: true });

    const cerca = async (q) => {
      if (!q || q.length < 2) return;
      cercando.value = true;
      try {
        const r = await partnerAPI.getPartnersPaged({ search: q, limit: 20 });
        partner.value = r.rows;
      } catch (e) {
        console.warn('Ricerca partner fallita:', e?.message);
      } finally {
        cercando.value = false;
      }
    };

    // Le sedi sono del partner: cambiato il partner, quella di prima non vale.
    const onPartner = async (id) => {
      sedeId.value = '';
      anteprima.value = null;
      await caricaSedi(id);
      // La sede di adesso non è una scelta: si propone solo se è l'unica
      // diversa da quella della prenotazione.
      const altre = sedi.value.filter((s) => s.id !== props.booking?.headquarter?.id);
      if (altre.length === 1) {
        sedeId.value = altre[0].id;
        calcola();
      }
    };

    const r2 = (n) => Math.round((Number(n) + Number.EPSILON) * 100) / 100;
    const leggi = (v) => {
      const n = Number(String(v ?? '').replace(/\s/g, '').replace(',', '.'));
      return String(v ?? '').trim() !== '' && Number.isFinite(n) && n >= 0 ? r2(n) : null;
    };
    const scrivi = (n) => (n == null ? '' : r2(n).toFixed(2).replace('.', ','));

    // Ogni nuova anteprima propone i suoi importi, finché chi salva non li ha
    // scritti a mano. La spunta del pagamento in app parte da com'è adesso:
    // c'è una voce da pagare? Allora sì.
    watch(anteprima, (a) => {
      if (!a) return;
      if (!importiToccati.value) {
        imp.value = scrivi(a.prenotazione.imponibile.dopo);
        tot.value = scrivi(a.prenotazione.totale.dopo);
      }
      if (primaAnteprima) {
        inApp.value = !!a.pagamentoInApp?.voce;
        primaAnteprima = false;
      }
      if (!appToccato.value) importoApp.value = tot.value;
    });
    const onImp = () => {
      importiToccati.value = true;
      const n = leggi(imp.value);
      if (n != null) tot.value = scrivi(n * 1.22);
      if (!appToccato.value) importoApp.value = tot.value;
    };
    const onTot = () => {
      importiToccati.value = true;
      const n = leggi(tot.value);
      if (n != null) imp.value = scrivi(n / 1.22);
      if (!appToccato.value) importoApp.value = tot.value;
    };
    const ripristinaImporti = () => {
      importiToccati.value = false;
      imp.value = scrivi(anteprima.value?.prenotazione.imponibile.dopo);
      tot.value = scrivi(anteprima.value?.prenotazione.totale.dopo);
      if (!appToccato.value) importoApp.value = tot.value;
    };
    const importiValidi = computed(() => leggi(imp.value) != null && leggi(tot.value) != null
      && (!inApp.value || leggi(importoApp.value) != null));

    const corpo = (dryRun) => ({
      headquarterId: sedeId.value,
      dryRun,
      liberaDriver: liberaDriver.value,
      ...(pickup.value && cambiaPartner.value ? { usaValet: usaValet.value } : {}),
      // Gli importi si mandano solo se scritti a mano: altrimenti vale il
      // ricalcolo (che sul valet lo fa il consumo del pacchetto).
      ...(!dryRun && importiToccati.value ? { imponibile: leggi(imp.value), tot_fattura: leggi(tot.value) } : {}),
      ...(!dryRun ? {
        pagamentoInApp: inApp.value
          ? { attivo: true, importo: leggi(importoApp.value) }
          : { attivo: false },
      } : {}),
    });

    const calcola = async () => {
      if (!sedeId.value) return;
      if (sedeId.value === props.booking?.headquarter?.id) {
        anteprima.value = null;
        ElMessage.info('La prenotazione è già di questa sede.');
        return;
      }
      calcolando.value = true;
      try {
        const r = await bookingAPI.changePartnerSede(props.booking.id, corpo(true));
        anteprima.value = r.data;
      } catch (e) {
        // il messaggio del server lo mostra l'interceptor di API.js
        console.error('Calcolo non riuscito:', e?.response?.status);
        anteprima.value = null;
      } finally {
        calcolando.value = false;
      }
    };

    const salva = async () => {
      salvando.value = true;
      try {
        const r = await bookingAPI.changePartnerSede(props.booking.id, corpo(false));
        const v = r.data?.valet || {};
        let msg = r.data?.cambiaPartner ? 'Partner e sede cambiati.' : 'Sede cambiata.';
        if (v.prima && !v.dopo) msg += ' Il valet è tornato al pacchetto.';
        else if (r.data?.cambiaPartner && v.dopo) msg += ' Usato il valet del nuovo partner.';
        const avvisati = (r.data?.tratte || []).some((t) => t.turni && (t.assegnati?.driver || t.assegnati?.jolly));
        if (avvisati && !liberaDriver.value) msg += ' Driver avvisati.';
        ElMessage.success(msg);
        emit('update:modelValue', false);
        emit('saved');
      } catch (e) {
        console.error('Cambio partner e sede non salvato:', e?.response?.status);
      } finally {
        salvando.value = false;
      }
    };

    const valetTesto = computed(() => {
      const a = anteprima.value;
      if (!a) return '';
      const { prima, dopo } = a.valet || {};
      if (prima && !a.cambiaPartner) return 'Corsa col valet: il prezzo è l\'eccedenza km del pacchetto.';
      if (prima && dopo) return 'Il valet torna al pacchetto del partner di prima e si usa quello del nuovo partner.';
      if (prima && !dopo) return 'Il valet torna al pacchetto: il prezzo è quello delle tratte.';
      if (!prima && dopo) return 'Si usa il pacchetto valet dell\'auto col nuovo partner.';
      return '';
    });

    const turniDi = (t) => [
      t.turni.driver && { ruolo: 'Driver', ...t.turni.driver },
      t.turni.jolly && { ruolo: 'Jolly', ...t.turni.jolly },
    ].filter(Boolean);

    const hhmm = (v) => {
      const d = new Date(v);
      return Number.isNaN(d.getTime())
        ? '—'
        : `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    };
    const orario = (x) => (x ? `${hhmm(x.inizio)}–${hhmm(x.fine)}` : '—');
    const euro = (n) => (n == null ? '—' : `€ ${Number(n).toFixed(2).replace('.', ',')}`);
    const numero = (n) => (n == null ? '—' : String(Math.round(Number(n) * 10) / 10).replace('.', ','));
    const cambia = (x) => x && String(x.prima) !== String(x.dopo);

    return {
      partnerId, sedeId, partner, sedi, cercando, caricandoSedi, calcolando, salvando,
      anteprima, liberaDriver, usaValet, pickup, cambiaPartner, valetTesto,
      imp, tot, importiToccati, inApp, importoApp, appToccato, importiValidi, onImp, onTot, ripristinaImporti,
      indirizzo, cerca, onPartner, calcola, salva, turniDi, orario, euro, numero, cambia,
    };
  },
};
</script>

<style scoped>
/* Il corpo del dialog di Element Plus spezza le parole a metà (break-all). */
.psd { display: flex; flex-direction: column; gap: 14px; word-break: normal; }
.psd__sel { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.psd__fld label {
  display: block; margin-bottom: 4px; font-size: 10.5px; font-weight: 700;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-tertiary);
}
.psd__opt { margin-left: 8px; font-size: 12px; color: var(--text-tertiary); }
.psd__hint { margin: 6px 0 0; font-size: 11.5px; line-height: 1.45; color: var(--text-tertiary); }
.psd__hint--attn { color: var(--state-attn); }
.psd__cmp { border-top: 1px solid var(--border-color); padding-top: 8px; transition: opacity 0.15s; }
.psd__cmp.is-loading { opacity: 0.55; }
.psd__row {
  display: grid; grid-template-columns: 1.4fr 1fr 1fr; gap: 10px; padding: 6px 0;
  font-size: 13px; border-bottom: 1px solid var(--border-color);
  font-variant-numeric: tabular-nums; color: var(--text-secondary);
}
.psd__row > * { min-width: 0; overflow-wrap: break-word; }
/* Le spunte vanno a capo come il testo, invece di allargare il dialog. */
.psd :deep(.el-checkbox) { height: auto; white-space: normal; align-items: flex-start; }
.psd :deep(.el-checkbox__label) { white-space: normal; line-height: 1.4; }
.psd__row em { font-style: normal; color: var(--text-tertiary); }
.psd__row b { color: var(--text-primary); font-weight: 600; }
.psd__row--head {
  font-size: 10.5px; font-weight: 700; letter-spacing: 0.05em;
  text-transform: uppercase; color: var(--text-tertiary);
}
.psd__row--tot { margin-top: 8px; border-top: 1px solid var(--border-color); }
.psd__row--in { align-items: center; }
.psd__row--app { margin-top: 8px; }
.psd__in { width: 100%; max-width: 150px; }
.psd__leg {
  margin-top: 12px; padding-bottom: 2px; font-size: 11px; font-weight: 700;
  letter-spacing: 0.05em; text-transform: uppercase; color: var(--text-primary);
}
.psd__chg { color: var(--primary) !important; }
.psd__warn {
  margin-top: 10px; padding: 10px 12px; border-radius: 6px;
  background: var(--state-attn-bg); border: 1px solid var(--state-attn-line);
  color: var(--state-attn); font-size: 12.5px;
}
.psd__warn p { margin: 0 0 6px; }
@media (max-width: 640px) {
  .psd__sel { grid-template-columns: 1fr; }
}
</style>
